import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../../components/buttons/button';
import { Input } from '../../../components/ui/input';
import { entityService } from '../../../services/entityService';
import { useGeoLocation } from '../../../hooks/useGeoLocation';
import { toastService } from '../../../services/toastService';
import { useSync } from '../../../hooks/useSync';
import EntityCreationMiniForm from './EntityCreationMiniForm';
import { ENTITY_LABELS } from '../../../constants/registryLabels';

// ─── Icons ────────────────────────────────────────────────────────────────────

const NearbyIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const EyeIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const RefreshIcon = ({ spinning = false }) => (
  <svg
    className={`w-4 h-4 ${spinning ? 'animate-spin' : ''}`}
    fill="none" viewBox="0 0 24 24" stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const RecordIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

// ─── Tab config ───────────────────────────────────────────────────────────────

const ALL_TABS = [
  { id: 'nearest', label: 'Nearest', Icon: NearbyIcon, requiresOnline: true },
  { id: 'search',  label: 'Search',  Icon: SearchIcon,  requiresOnline: true },
  { id: 'create',  label: 'New',     Icon: PlusIcon,    requiresOnline: false, onlyForCreate: true },
];

const SearchableFieldsList = ({ record }) => {
  const fields = record?.entity_type?.searchable_fields;
  if (!Array.isArray(fields) || fields.length === 0) return null;
  
  const schema = record?.entity_type?.schema_json || [];
  
  const displayItems = fields.map(key => {
    const value = record?.attributes_json?.[key];
    if (value === undefined || value === null || value === '') return null;
    
    const schemaField = schema.find(f => f.key === key);
    const label = schemaField ? schemaField.label : key;
    
    return { key, label, value };
  }).filter(Boolean);
  
  if (displayItems.length === 0) return null;
  
  return (
    <div className="mt-1.5 flex flex-col gap-1">
      {displayItems.map(item => (
        <span key={item.key} className="text-xs text-gray-500 truncate">
          <span className="font-medium text-gray-700">{item.label}:</span> {item.value}
        </span>
      ))}
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const EntitySelectionPage = ({ form, onEntitySelected }) => {
  const rawMode = form?.entity_action_default || 'create';
  const actionMode = String(rawMode).trim().toLowerCase(); 
  
  // Tab is now available for all modes (restored for offline functionality)
  const TABS = ALL_TABS;
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOnline } = useSync();
  const [searchQuery, setSearchQuery] = useState('');
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const pageRef = useRef(1); // always current — avoids stale closure in handleLoadMore
  const recordsCacheRef = useRef([]);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const { locationData, isLocationLoading, refreshLocation, permissionDenied, positionUnavailable, locationFetched } = useGeoLocation();
  const gpsBlocked = permissionDenied || positionUnavailable || (locationFetched && !locationData?.latitude);

  // Helper: update both state and ref atomically
  const updatePage = (p) => { pageRef.current = p; setPage(p); };

  const extractItems = useCallback((response) => {
    const resData = response?.data || response;
    let items = Array.isArray(resData) 
      ? resData 
      : Array.isArray(resData?.data) 
        ? resData.data 
        : Array.isArray(resData?.entities)
          ? resData.entities
          : [];
          
    if (resData && !Array.isArray(resData) && Array.isArray(resData.entities)) {
      const entityType = { ...resData };
      delete entityType.entities;
      items = items.map(item => ({ ...item, entity_type: item.entity_type || entityType }));
    }
    return items;
  }, []);

  const PAGE_SIZE = 10;

  const activeTab = searchParams.get('tab') || (isOnline ? 'nearest' : 'create');
  const recordIdFromUrl = searchParams.get('entity_id');

  // ── Sync selected record with URL on refresh ─────────────────────────────
  useEffect(() => {
    if (!recordIdFromUrl) {
      if (selectedRecord) {
        setSelectedRecord(null);
      }
      return;
    }

    if (recordIdFromUrl && (!selectedRecord || selectedRecord.id !== recordIdFromUrl)) {
      const fetchSelected = async () => {
        setLoading(true);
        try {
          const response = await entityService.getById(recordIdFromUrl);
          setSelectedRecord(response?.data || response);
        } catch {
          toastService.error('Failed to load selected record');
          setSearchParams(params => { params.delete('entity_id'); return params; }, { replace: true });
        } finally {
          setLoading(false);
        }
      };
      fetchSelected();
    }
  }, [recordIdFromUrl, selectedRecord, setSearchParams]);

  // ── Data fetchers ────────────────────────────────────────────────────────
  const fetchNearest = useCallback(async (pageNum = 1) => {
    if (!locationData?.latitude || !isOnline) return;
    
    if (pageNum > 1) {
      setLoadingMore(true);
      setTimeout(() => {
        const nextItems = recordsCacheRef.current.slice((pageNum - 1) * PAGE_SIZE, pageNum * PAGE_SIZE);
        setRecords(prev => [...prev, ...nextItems]);
        setHasMorePages(recordsCacheRef.current.length > pageNum * PAGE_SIZE);
        updatePage(pageNum);
        setLoadingMore(false);
      }, 300);
      return;
    }

    setLoading(true);
    try {
      const response = await entityService.list({
        near: `${locationData.latitude},${locationData.longitude}`,
        entity_type_id: form.linked_entity_type_id,
        form_id: form.id,
      });
      const items = extractItems(response);
      
      recordsCacheRef.current = items;
      const firstPageItems = items.slice(0, PAGE_SIZE);
      setRecords(firstPageItems);
      setHasMorePages(items.length > PAGE_SIZE);
      updatePage(1);
    } catch {
      toastService.error('Failed to fetch nearby records');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [locationData, form.linked_entity_type_id, form.id, isOnline, PAGE_SIZE]);

  const fetchAll = useCallback(async (pageNum = 1) => {
    if (!isOnline) return;
    
    if (pageNum > 1) {
      setLoadingMore(true);
      setTimeout(() => {
        const nextItems = recordsCacheRef.current.slice((pageNum - 1) * PAGE_SIZE, pageNum * PAGE_SIZE);
        setRecords(prev => [...prev, ...nextItems]);
        setHasMorePages(recordsCacheRef.current.length > pageNum * PAGE_SIZE);
        updatePage(pageNum);
        setLoadingMore(false);
      }, 300);
      return;
    }

    setLoading(true);
    try {
      const response = await entityService.list({ 
        form_id: form.id
      });
      const items = extractItems(response);
      
      recordsCacheRef.current = items;
      const firstPageItems = items.slice(0, PAGE_SIZE);
      setRecords(firstPageItems);
      setHasMorePages(items.length > PAGE_SIZE);
      updatePage(1);
    } catch {
      toastService.error('Failed to fetch records');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [isOnline, form.id, PAGE_SIZE]);

  const fetchSearch = useCallback(async (query, pageNum = 1) => {
    if (!isOnline) { toastService.warning('Search is not available offline'); return; }
    if (!query.trim()) { fetchAll(pageNum); return; }
    
    if (pageNum > 1) {
      setLoadingMore(true);
      setTimeout(() => {
        const nextItems = recordsCacheRef.current.slice((pageNum - 1) * PAGE_SIZE, pageNum * PAGE_SIZE);
        setRecords(prev => [...prev, ...nextItems]);
        setHasMorePages(recordsCacheRef.current.length > pageNum * PAGE_SIZE);
        updatePage(pageNum);
        setLoadingMore(false);
      }, 300);
      return;
    }

    setLoading(true);
    try {
      const response = await entityService.list({
        q: query,
        entity_type_id: form.linked_entity_type_id,
        form_id: form.id,
      });
      const items = extractItems(response);
      
      recordsCacheRef.current = items;
      const firstPageItems = items.slice(0, PAGE_SIZE);
      setRecords(firstPageItems);
      setHasMorePages(items.length > PAGE_SIZE);
      updatePage(1);
    } catch {
      toastService.error('Search failed');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [isOnline, form.linked_entity_type_id, form.id, PAGE_SIZE, fetchAll]);

  const handleSearch = (e) => {
    e?.preventDefault();
    fetchSearch(searchQuery, 1);
  };

  useEffect(() => {
    if (activeTab !== 'search') return;
    const timer = setTimeout(() => {
      fetchSearch(searchQuery, 1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, activeTab, fetchSearch]);

  // Load next page and append — reads pageRef.current to avoid stale closure
  const handleLoadMore = useCallback(() => {
    const nextPage = pageRef.current + 1;
    if (activeTab === 'nearest') fetchNearest(nextPage);
    else if (activeTab === 'search') fetchSearch(searchQuery, nextPage);
  }, [activeTab, fetchNearest, fetchSearch, searchQuery]);


  useEffect(() => {
    if (recordIdFromUrl) return;
    if (activeTab === 'nearest' && locationData?.latitude && isOnline) fetchNearest();
    else if (activeTab === 'search' && isOnline) fetchAll();
  }, [activeTab, locationData, fetchNearest, fetchAll, recordIdFromUrl, isOnline]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleTabChange = (tabId) => {
    if (!isOnline && (tabId === 'nearest' || tabId === 'search')) {
      toastService.warning(`${tabId === 'nearest' ? 'Nearest' : 'Search'} records are not available offline.`);
      return;
    }
    setSearchParams(params => { params.set('tab', tabId); params.delete('entity_id'); return params; }, { replace: true });
    setRecords([]);
    setPage(1);
    setHasMorePages(false);
  };

  const handleSelectForPreview = (record) => {
    setSelectedRecord(record);
    setSearchParams(params => { params.set('entity_id', record.id); return params; }, { replace: true });
  };

  const handleBackFromPreview = () => {
    setSelectedRecord(null);
    setSearchParams(params => { params.delete('entity_id'); return params; }, { replace: true });
  };

  // ── Record list items ────────────────────────────────────────────────────
  const renderRecordList = () => {
    if (loading && !recordIdFromUrl) {
      return (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl border-2 border-gray-100 p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      );
    }

    if (records.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-14 text-center">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <RecordIcon />
          </div>
          <p className="text-gray-900 font-bold text-base mb-1">{ENTITY_LABELS.NO_RECORDS}</p>
          <p className="text-gray-500 text-sm">
            {activeTab === 'nearest'
              ? ENTITY_LABELS.NO_RECORDS_NEARBY
              : 'Try a different search term.'}
          </p>
          {activeTab === 'nearest' && isOnline && (
            <button
              onClick={fetchNearest}
              className="mt-4 text-[var(--btn-primary-bg)] text-sm font-bold flex items-center gap-1.5 hover:underline"
            >
              <RefreshIcon /> Retry
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {records.map(record => (
          <div
            key={record.id}
            className="w-full flex items-stretch bg-white rounded-xl border-2 border-gray-200 hover:border-[var(--btn-primary-bg)] hover:shadow-md transition-all duration-200 shadow-sm group overflow-hidden"
          >
            <button
              type="button"
              onClick={() => onEntitySelected(record)}
              className="flex-1 text-left p-4 min-w-0 focus:outline-none active:bg-gray-50"
            >
              <h4 className="font-bold text-gray-900 text-sm truncate group-hover:text-[var(--btn-primary-bg)] transition-colors">
                {record.primary_value || ENTITY_LABELS.UNNAMED_RECORD}
              </h4>
              <SearchableFieldsList record={record} />
              {record.distance !== undefined && (
                <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-[var(--btn-primary-bg)] uppercase tracking-wider">
                  <NearbyIcon />
                  {Math.round(record.distance)}m away
                </div>
              )}
            </button>
            <div className="shrink-0 flex items-center pr-2 border-l border-transparent group-hover:border-gray-100">
              {record.submissions_count > 0 && (
                <span className="text-[10px] font-bold bg-gray-100 text-gray-600 px-2 py-1 rounded-md mr-2">
                  {record.submissions_count} submissions
                </span>
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleSelectForPreview(record); }}
                className="p-3 text-gray-400 hover:text-[var(--btn-primary-bg)] hover:bg-[var(--btn-primary-bg)]/10 rounded-lg transition-colors focus:outline-none"
                aria-label="Preview Record"
              >
                <EyeIcon />
              </button>
            </div>
          </div>
        ))}

        {hasMorePages && (
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="w-full mt-1 py-3 rounded-xl bg-white border-2 border-gray-200 text-sm font-bold text-gray-600 hover:border-[var(--btn-primary-bg)] hover:text-[var(--btn-primary-bg)] hover:bg-[var(--btn-primary-bg)]/5 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm"
          >
            {loadingMore ? (
              <>
                <svg className="w-4 h-4 animate-spin text-[var(--btn-primary-bg)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="text-[var(--btn-primary-bg)]">Loading…</span>
              </>
            ) : (
              <>
                View more
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                </svg>
              </>
            )}
          </button>
        )}
      </div>
    );
  };

  if (selectedRecord || (recordIdFromUrl && loading)) {
    // Heading and readOnly based on mode
    const isUpdate = actionMode.includes('update');
    const isLog = actionMode.includes('log');

    // Heading derivation
    let confirmHeading = 'Confirm Selection';
    if (isUpdate) confirmHeading = ENTITY_LABELS.UPDATE_RECORD;
    else if (isLog) confirmHeading = ENTITY_LABELS.LOG_RECORD;
    
    // Only 'update' mode allows editing the record attributes here
    const miniFormReadOnly = !isUpdate; 

    return (
      <div className="flex flex-col h-full">
        <div className="mb-6">
          <h2 className="text-xl font-black text-gray-900 leading-tight">
            {confirmHeading}
          </h2>
          {selectedRecord?.code && (
            <p className="text-sm text-gray-500 mt-1 font-medium">
              {ENTITY_LABELS.RECORD}: <span className="font-bold text-gray-700">{selectedRecord.code}</span>
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-[var(--btn-primary-bg)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <EntityCreationMiniForm
            form={form}
            initialData={selectedRecord}
            readOnly={miniFormReadOnly}
            mode={actionMode}
            onEntityCreated={onEntitySelected}
          />
        )}
      </div>
    );
  }

  // ── Main selection screen ────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full">
      {/* Big intro heading — matches old styling */}
      <div className="mb-5">
        <h2 className="text-xl font-black text-gray-900 leading-tight">
          {actionMode === 'update' ? ENTITY_LABELS.SELECT_RECORD_TO_UPDATE
            : actionMode === 'log' ? ENTITY_LABELS.SELECT_RECORD_TO_LOG
            : <>{ENTITY_LABELS.SELECT_OR_CREATE} <br /><span className="text-[var(--btn-primary-bg)]">{form.name} {ENTITY_LABELS.RECORD}</span></>}
        </h2>
        <p className="text-sm text-gray-500 mt-2 font-medium">
          {ENTITY_LABELS.LINK_REQUIRED}
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-4 shrink-0">
        <div className="flex gap-1">
          {TABS.map(({ id, label, Icon, requiresOnline }) => {
            const isDisabled = requiresOnline && !isOnline;
            const isActive = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => handleTabChange(id)}
                disabled={isDisabled}
                className={`flex items-center gap-1.5 px-4 py-3 text-xs font-bold uppercase tracking-widest border-b-2 transition-all relative ${
                  isActive
                    ? 'text-[var(--btn-primary-bg)] border-[var(--btn-primary-bg)]'
                    : 'text-gray-400 border-transparent hover:text-gray-600'
                } ${isDisabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <Icon />
                {label}
                 
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto px-4 py-5 pb-24">
        {/* ── Nearest tab ── */}
        {activeTab === 'nearest' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                Within 200m of you
              </p>
              {!gpsBlocked && (
                <button
                  onClick={fetchNearest}
                  disabled={isLocationLoading || loading}
                  className="flex items-center gap-1.5 text-xs font-bold text-[var(--btn-primary-bg)] hover:opacity-80 active:scale-95 transition-all disabled:opacity-40"
                >
                  <RefreshIcon spinning={isLocationLoading || loading} />
                  Refresh
                </button>
              )}
            </div>

            {/* GPS loading */}
            {isLocationLoading && (
              <div className="flex flex-col items-center justify-center py-14 text-center">
                <div className="w-10 h-10 border-4 border-[var(--btn-primary-bg)] border-t-transparent rounded-full animate-spin mb-3" />
                <p className="text-sm text-gray-500 font-medium">Getting your GPS location…</p>
              </div>
            )}

            {/* GPS blocked — show enable prompt */}
            {!isLocationLoading && gpsBlocked && (
              <div className="flex flex-col items-center justify-center py-10 text-center px-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <NearbyIcon />
                </div>
                <p className="text-gray-900 font-bold text-base mb-1">
                  {permissionDenied ? 'Location Permission Blocked' : 'GPS is Disabled'}
                </p>
                <p className="text-gray-500 text-sm mb-5">
                  {permissionDenied
                    ? 'Enable location permission in your browser or device settings to find nearby records.'
                    : 'Turn on your device GPS to find records near you.'}
                </p>
                <button
                  onClick={refreshLocation}
                  disabled={isLocationLoading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--btn-primary-bg)] text-white text-sm font-bold rounded-xl hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50"
                >
                  <RefreshIcon spinning={isLocationLoading} />
                  {isLocationLoading ? 'Fetching…' : 'Try Again'}
                </button>
              </div>
            )}

            {/* Records list */}
            {!isLocationLoading && !gpsBlocked && renderRecordList()}
          </div>
        )}

        {/* ── Search tab ── */}
        {activeTab === 'search' && (
          <div className="space-y-4">
            <form onSubmit={handleSearch} className="relative">
              <Input
                placeholder="Search by name or ID…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pr-10"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Clear search"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </form>
            {renderRecordList()}
          </div>
        )}

        {/* ── Create tab ── */}
        {activeTab === 'create' && (
          <EntityCreationMiniForm
            form={form}
            onEntityCreated={onEntitySelected}
          />
        )}
      </div>
    </div>
  );
};

export default EntitySelectionPage;
